import React, { useState, useEffect } from 'react';
import { faqAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  MessageCircle,
  Plus,
  Pencil,
  Trash2,
  ChefHat,
  Users,
  Store,
  HelpCircle,
  Download,
  CheckCircle,
  XCircle
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';

const CATEGORY_INFO = {
  homecook: { label: 'HomeCooks', icon: ChefHat, color: 'text-orange-500', bgColor: 'bg-orange-100' },
  resident: { label: 'Residents', icon: Users, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  vendor: { label: 'Vendors', icon: Store, color: 'text-green-500', bgColor: 'bg-green-100' }
};

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('homecook');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    category: 'homecook',
    question: '',
    answer: '',
    order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    setLoading(true);
    try {
      const response = await faqAPI.getAll(null, false); // Get all including inactive
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedFAQs = async () => {
    try {
      const response = await faqAPI.seed();
      if (response.data.success) {
        toast.success(response.data.message);
        loadFAQs();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to seed FAQs');
    }
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    setSaving(true);
    try {
      if (editingFaq) {
        await faqAPI.update(editingFaq.id, {
          question: formData.question,
          answer: formData.answer,
          order: formData.order
        });
        toast.success('FAQ updated');
      } else {
        await faqAPI.create(formData);
        toast.success('FAQ created');
      }
      setShowModal(false);
      resetForm();
      loadFAQs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      order: faq.order
    });
    setShowModal(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await faqAPI.delete(faqId);
      toast.success('FAQ deleted');
      loadFAQs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await faqAPI.update(faq.id, { is_active: !faq.is_active });
      toast.success(faq.is_active ? 'FAQ deactivated' : 'FAQ activated');
      loadFAQs();
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({
      category: activeTab,
      question: '',
      answer: '',
      order: 0
    });
  };

  const openAddModal = () => {
    resetForm();
    setFormData(prev => ({ ...prev, category: activeTab }));
    setShowModal(true);
  };

  // Filter FAQs by category
  const filteredFaqs = faqs.filter(faq => faq.category === activeTab);

  // Stats
  const stats = {
    homecook: faqs.filter(f => f.category === 'homecook').length,
    resident: faqs.filter(f => f.category === 'resident').length,
    vendor: faqs.filter(f => f.category === 'vendor').length,
    total: faqs.length,
    active: faqs.filter(f => f.is_active).length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-faqs">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-orange-500" />
              Cravy FAQ Management
            </h1>
            <p className="text-muted-foreground">Manage chatbot FAQs for HomeCooks, Residents & Vendors</p>
          </div>
          <div className="flex gap-2">
            {faqs.length === 0 && (
              <Button 
                variant="outline"
                onClick={handleSeedFAQs}
                data-testid="seed-faqs-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Load Default FAQs
              </Button>
            )}
            <Button 
              onClick={openAddModal}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="add-faq-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <ChefHat className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">HomeCook FAQs</p>
                  <p className="text-2xl font-bold">{stats.homecook}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Resident FAQs</p>
                  <p className="text-2xl font-bold">{stats.resident}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Store className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendor FAQs</p>
                  <p className="text-2xl font-bold">{stats.vendor}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total / Active</p>
                  <p className="text-2xl font-bold">{stats.total} / {stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Tabs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-500" />
              Frequently Asked Questions
            </CardTitle>
            <CardDescription>
              Click on a category tab to view and manage FAQs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="homecook" className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4" />
                  HomeCooks ({stats.homecook})
                </TabsTrigger>
                <TabsTrigger value="resident" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Residents ({stats.resident})
                </TabsTrigger>
                <TabsTrigger value="vendor" className="flex items-center gap-2">
                  <Store className="w-4 h-4" />
                  Vendors ({stats.vendor})
                </TabsTrigger>
              </TabsList>

              {['homecook', 'resident', 'vendor'].map(category => (
                <TabsContent key={category} value={category}>
                  {filteredFaqs.length === 0 ? (
                    <div className="text-center py-8">
                      <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-muted-foreground">No FAQs for {CATEGORY_INFO[category].label} yet.</p>
                      <Button 
                        className="mt-4"
                        onClick={openAddModal}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First FAQ
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Question</TableHead>
                          <TableHead>Answer</TableHead>
                          <TableHead className="w-20">Active</TableHead>
                          <TableHead className="w-24 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredFaqs.sort((a, b) => a.order - b.order).map((faq, idx) => (
                          <TableRow key={faq.id} data-testid={`faq-row-${faq.id}`}>
                            <TableCell className="font-mono text-sm text-muted-foreground">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="font-medium max-w-xs">
                              <p className="line-clamp-2">{faq.question}</p>
                            </TableCell>
                            <TableCell className="max-w-md">
                              <p className="line-clamp-2 text-muted-foreground">{faq.answer}</p>
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={faq.is_active}
                                onCheckedChange={() => handleToggleActive(faq)}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEdit(faq)}
                                  data-testid={`edit-faq-${faq.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDelete(faq.id)}
                                  data-testid={`delete-faq-${faq.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit FAQ Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-500" />
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </DialogTitle>
            <DialogDescription>
              {editingFaq ? 'Update the FAQ details' : 'Create a new FAQ for the chatbot'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Category *</Label>
              <Select 
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                disabled={!!editingFaq}
              >
                <SelectTrigger data-testid="faq-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="homecook">
                    <span className="flex items-center gap-2">
                      <ChefHat className="w-4 h-4 text-orange-500" />
                      HomeCooks
                    </span>
                  </SelectItem>
                  <SelectItem value="resident">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      Residents
                    </span>
                  </SelectItem>
                  <SelectItem value="vendor">
                    <span className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-green-500" />
                      Vendors
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                placeholder="e.g., Do I need an FSSAI license?"
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                data-testid="faq-question-input"
              />
            </div>
            
            <div>
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                placeholder="Enter the answer..."
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                rows={4}
                data-testid="faq-answer-input"
              />
            </div>
            
            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                type="number"
                placeholder="0"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                data-testid="faq-order-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower numbers appear first in the list
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-faq-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : null}
                {editingFaq ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
